import { cn } from "@/lib/cn";
import type { VariantProps } from "class-variance-authority";
import { forwardRef, type ButtonHTMLAttributes, type ReactNode, type Ref } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { cardSurface, cardVariants } from "./Card";
import { SurfaceProvider } from "./Surface";

const INTERACTION = [
  "block text-left select-none",
  "transition-[border-color,box-shadow,transform] duration-base ease-standard",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 focus-visible:ring-offset-page",
];

const LINK_INTERACTION = { base: "aria-disabled:pointer-events-none", lift: "hover:shadow-card-hover motion-safe:active:scale-[0.98]" };

const BUTTON_INTERACTION = {
  base: "disabled:cursor-not-allowed",
  lift: "enabled:hover:shadow-card-hover enabled:motion-safe:active:scale-[0.98]",
};

const DIM_WHEN_DISABLED = "disabled:opacity-60 aria-disabled:opacity-60";

const OUTLINE_INTERACTION = "hover:border-primary focus-visible:ring-primary focus-visible:ring-offset-0";

const GAME_INTERACTION = "hover:border-game-gold-deep focus-visible:ring-game-gold focus-visible:ring-offset-game-ivory";

type CardLook = Omit<VariantProps<typeof cardVariants>, "interactive"> & {
  /** `lift` (default) raises the shadow and presses; `outline` only rings the border in the tone on hover and focus (choice lists). */
  interaction?: "lift" | "outline";
  /** `false` when the content draws its own disabled look (e.g. dims only the art, keeps a status badge legible). */
  dimWhenDisabled?: boolean;
  className?: string;
  children?: ReactNode;
};

type ButtonCard = CardLook & { to?: never } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "className" | "children">;
type LinkCard = CardLook & Omit<LinkProps, "className" | "children">;

/** Props of `InteractiveCard`: with `to` it is a react-router Link, otherwise a `type="button"` button. */
export type InteractiveCardProps = ButtonCard | LinkCard;

type LinkRest = Omit<LinkCard, keyof CardLook>;
type ButtonRest = Omit<ButtonCard, keyof CardLook>;

function isLinkRest(rest: LinkRest | ButtonRest): rest is LinkRest {
  return rest.to !== undefined;
}

/** A whole-clickable Card (rail item, suggestion, coupon, prompt): card look plus focus ring, hover shadow and press. */
export const InteractiveCard = forwardRef<HTMLButtonElement | HTMLAnchorElement, InteractiveCardProps>(
  function InteractiveCard(
    { variant, tone, padding, radius, highlight, dimmed, accent, interaction = "lift", dimWhenDisabled = true, className, children, ...rest },
    ref,
  ) {
    const classes = (element: { base: string; lift: string }) =>
      cn(
        cardVariants({ variant, tone, padding, radius, highlight, dimmed, accent }),
        INTERACTION,
        element.base,
        interaction === "lift" ? element.lift : OUTLINE_INTERACTION,
        variant === "game" && GAME_INTERACTION,
        dimWhenDisabled && DIM_WHEN_DISABLED,
        className,
      );
    const surface = cardSurface(variant, tone);
    const content = surface ? <SurfaceProvider surface={surface}>{children}</SurfaceProvider> : children;

    if (isLinkRest(rest)) {
      return (
        <Link {...rest} ref={ref as Ref<HTMLAnchorElement>} className={classes(LINK_INTERACTION)}>
          {content}
        </Link>
      );
    }
    const { type = "button", ...button } = rest;
    return (
      <button {...button} ref={ref as Ref<HTMLButtonElement>} type={type} className={classes(BUTTON_INTERACTION)}>
        {content}
      </button>
    );
  },
);
